import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatAppointmentResponseBody,
  isSuccessfulAppointmentResponse,
  readAppointmentResponseBody,
} from '../appointmentResponse';
import type { AppointmentFull } from '../backendInterfaces';
import { AppointmentResponseErrorDialog } from '../components/AppointmentResponseErrorDialog';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import type { FullUser } from './ProfilePage';
import { AdminPatientSearch } from '../components/AdminPatientSearch';
import './AppointmentChainPage.css';
//import { type Procedure } from './AdminBookingPage';

const categoryItems = ['surgery', 'endo', 'prosthetics', 'ortho', 'various'];
const allCategoryItems = ['control', 'filling', 'surgery', 'endo', 'prosthetics', 'ortho', 'exam', 'various', 'proflaxis'];
const practitionerIds = ['data_c_10', 'data_c_11', 'data_c_12', 'data_c_13', 'data_c_14', 'data_c_15'];

type ProcedureRuleMap = {
  min?: string;
  max?: string;
  chainTitle?: string;
  linkedIds?: string[];
  [key: string]: string | string[] | undefined;
};

export interface Procedure {
  id: string;
  customerId: string;
  type: string;
  name: string;
  duration: number;
  category: string;
  ruleMap: ProcedureRuleMap;
}

/**
 *  Get all fields of User and UserDetails with some Practitioner fields added.
 *  Map fields:
 *  - userId (from User)
 *  - userName (from User)
 *  - firstName (from User)
 *  - familyName (from User)
 *  - email (from User)
 *  - mobile (from User)
 *  - customerId (from User)
 *  - userDetailId (from User)
 *  - extId (from UserDetail)
 *  - salutation (from UserDetail)
 *  - title (from UserDetail)
 *  - addressLines (from UserDetail)
 *  - zipCode (from UserDetail)
 *  - town (from UserDetail)
 *  - languages (from UserDetail)
 *  - phones (from UserDetail)
 *  - occupation (from UserDetail)
 *  - dateOfBirth (from UserDetail)
 *  - adminLevel (from UserDetail)
 *  - comment (from UserDetail)
 *  - practitionerId (from Practitioner)
 *  - initials (from Practitioner)
 *  - officeId (from Practitioner)
 *  - patientType (from Practitioner)
 *  - procedureIds (from Practitioner)
 *  - photo (from Practitioner)
 */

// TODO: rename to PractitionerFull
interface FullPractitioner {
  // the FullUser fields:
  //id: string; // DEPRECATED, use userId and do not mix up with practitionerId below...
  userId: string;
  customerId: string;
  userName: string;
  firstName: string;
  familyName: string;
  email: string;
  mobile: string;
  userDetailId: string;
  extId: string;
  salutation: string;
  title: string;
  zipCode: string;
  town: string;
  addressLines: string[];
  languages: string[];
  dateOfBirth: string;
  comment: string;
  // the fields from Practitioner:
  practitionerId: string,
  initials: string,
  officeId: string;
  practitionerType: string;
  procedureIds: string[];
  photo: string;
  //practitionerInfo: JSON; // not yet
}

/* from backendInterface.ts:
export interface AppointmentFull {
  id: string;
  meetingId: string;
  status: string;
  description: string;
  appointmentInfoMap: string[];
  when: string;
  duration: number;
  type: string;
  userIds: string[];
  procedureIds: string[];
  practitionerIds: string[];
  patientIds: string[];
  practitionerExtIds: string[];
  patientExtIds: string[];
  meetingInfoMap: string[];
}
*/

type ChainAppointment = {
  id: string;
  procedureIds: string[];
  when?: string;
  duration: number;
  description: string;
  appointmentInfoMap: ProcedureRuleMap;
  practitionerId: string;
  isManuallyAdded?: boolean;
};

// /api/appointments/propose-chain-dates
type ProposedChainDatesResult = {
  message: string;
  dateChoices: string[][];
};

type AppointmentChainDraft = {
  version: 1;
  updatedAt: string;
  startDate: string;
  endDate: string;
  selectedCategory: string;
  selectedMainProcedureId: string;
  selectedPractitionerId: string;
  selectedPatient: Pick<FullUser, 'id' | 'title' | 'firstName' | 'familyName' | 'dateOfBirth'> | null;
  selectedPatientId: string;
  chainAppointments: ChainAppointment[];
  proposedDates?: string[];
  editingProcedureId?: string;
  editingIndex?: number;
  manualPlacement?: boolean;
  editingProcedureName?: string;
};

const addProposedDatesToDraftAppointments = (
  chainAppointments: ChainAppointment[],
  proposedDates: string[]
) => chainAppointments.map((appointment, index) => {
  const proposedDate = proposedDates[index];
  return proposedDate && !appointment.when
    ? { ...appointment, when: proposedDate }
    : appointment;
});

const restoreChainAppointments = (draft: AppointmentChainDraft | null) => {
  if (!draft) return [];

  return draft.chainAppointments.map((appointment, index) =>
    draft.proposedDates?.[index] && draft.proposedDates[index] === appointment.when
      ? { ...appointment, when: undefined }
      : appointment
  );
};

const appointmentChainResetEvent = 'appointment-chain-reset';
const appointmentChainDraftMaxAge = 24 * 60 * 60 * 1000;

const readAppointmentChainDraft = (): AppointmentChainDraft | null => {
  const storedDraft = localStorage.getItem('currentAppointmentChain');
  if (!storedDraft) {
    return null;
  }

  try {
    const draft = JSON.parse(storedDraft) as Partial<AppointmentChainDraft>;
    const updatedAt = typeof draft.updatedAt === 'string' ? new Date(draft.updatedAt) : null;
    const isExpired = !updatedAt || Number.isNaN(updatedAt.getTime()) || Date.now() - updatedAt.getTime() > appointmentChainDraftMaxAge;

    if (
      draft.version !== 1 ||
      isExpired ||
      typeof draft.startDate !== 'string' ||
      typeof draft.endDate !== 'string' ||
      typeof draft.selectedCategory !== 'string' ||
      typeof draft.selectedMainProcedureId !== 'string' ||
      typeof draft.selectedPractitionerId !== 'string' ||
      !Array.isArray(draft.chainAppointments)
    ) {
      localStorage.removeItem('currentAppointmentChain');
      return null;
    }

    return draft as AppointmentChainDraft;
  } catch {
    localStorage.removeItem('currentAppointmentChain');
    return null;
  }
};

type Office = {
  id: string;
  customerId: string;
  timeSlot: number;
  officeOpening: number;
  officeClosing: number;
  practitionerIds: string[];
};

// const storeAppointmentChain = () => {
//   console.log('storeAppointmentChain: ');
// };

const practitionerCache = new Map<string, FullPractitioner>();

const readStoredSelectedPractitionerId = () => {
  const storedPractitionerId = localStorage.getItem('selectedPractitionerId');
  return storedPractitionerId && practitionerIds.includes(storedPractitionerId) ? storedPractitionerId : '';
};

const formatCategoryLabel = (category: string) => {
  if (category === 'proflaxis') return 'Prophylaxe';
  const translatedName = t(category as TranslationKey) || category;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const formatProcedureLabel = (procedureName?: string) => {
  if (!procedureName) return '';
  const translatedName = t((`pro-${procedureName}`) as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const getProcedureChainTitle = (procedure?: Procedure) => {
  const chainTitle = procedure?.ruleMap?.chainTitle;
  if (chainTitle === null || chainTitle === undefined) {
    return '';
  }

  const normalizedChainTitle = String(chainTitle).trim();
  return normalizedChainTitle ? normalizedChainTitle.charAt(0).toUpperCase() + normalizedChainTitle.slice(1) : '';
};

const getPractitionerFullName = (practitioner?: FullPractitioner) => {
  if (!practitioner) {
    return '';
  }

  const title = String(practitioner.title ?? '').trim();
  const firstName = String(practitioner.firstName ?? '').trim();
  const familyName = String(practitioner.familyName ?? '').trim();

  return [title, firstName, familyName].filter(Boolean).join(' ').trim();
};

const getPractitionerInitials = (practitioner?: FullPractitioner) => {

  //console.log("getPractitionerInitials:");

  if (!practitioner) {
    return '??';
  }

  const practitionerInitials = String(practitioner.initials ?? '').trim();
  if (practitionerInitials) {

    //console.log("getPractitionerInitials: " + practitionerInitials);

    return practitionerInitials;
  }

  return '?';
};

const normalizeLinkedProcedureIds = (value: unknown, parseDepth = 0): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((linkedId): linkedId is string => typeof linkedId === 'string' && linkedId.trim() !== '')
      .map((linkedId) => linkedId.trim());
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (parsed !== trimmed && parseDepth < 2) {
        return normalizeLinkedProcedureIds(parsed, parseDepth + 1);
      }
    } catch {
      return trimmed
        .split(',')
        .map((linkedId) => linkedId.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const getLinkedProcedureIds = (procedure?: Procedure) => {
  return normalizeLinkedProcedureIds(procedure?.ruleMap?.linkedIds);
};

const isMainProcedure = (procedure?: Procedure) => getLinkedProcedureIds(procedure).length > 0;

const padTimePart = (value: number) => value.toString().padStart(2, '0');

const formatLocalDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const parseAppointmentDateTime = (value?: string) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const getDraftDateRange = (chainAppointments: ChainAppointment[] = [], editingIndex?: number) => {
  const now = new Date();
  const previousAppointmentDate = editingIndex !== undefined && editingIndex > 0
    ? parseAppointmentDateTime(chainAppointments[editingIndex - 1]?.when)
    : null;
  const currentAppointmentDate = editingIndex !== undefined
    ? parseAppointmentDateTime(chainAppointments[editingIndex]?.when)
    : null;
  const startDate = now;
  const endDate = new Date(currentAppointmentDate ?? previousAppointmentDate ?? now);
  endDate.setDate(endDate.getDate() + 14);

  return {
    startDate: formatLocalDateTime(startDate),
    endDate: formatLocalDateTime(endDate),
  };
};

const formatAppointmentDate = (value?: string) => {
  const parsed = parseAppointmentDateTime(value);
  if (!parsed) {
    return '';
  }

  return parsed.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatAppointmentTime = (value?: string) => {
  const parsed = parseAppointmentDateTime(value);
  if (!parsed) {
    return '';
  }

  return `${padTimePart(parsed.getHours())}:${padTimePart(parsed.getMinutes())}`;
};

const formatDateInputValue = (value?: string) => {
  const parsed = parseAppointmentDateTime(value);
  if (!parsed) {
    return '';
  }

  return `${parsed.getFullYear()}-${padTimePart(parsed.getMonth() + 1)}-${padTimePart(parsed.getDate())}`;
};

const formatCurrentDateInputValue = () => formatDateInputValue(formatLocalDateTime(new Date()));

const buildAppointmentDateTime = (dateValue: string, timeValue: string) => {
  const [yearValue, monthValue, dayValue] = dateValue.split('-').map(Number);
  const [hoursValue, minutesValue] = timeValue.split(':').map(Number);

  if (
    !Number.isFinite(yearValue) ||
    !Number.isFinite(monthValue) ||
    !Number.isFinite(dayValue) ||
    !Number.isFinite(hoursValue) ||
    !Number.isFinite(minutesValue)
  ) {
    return null;
  }

  const next = new Date(yearValue, monthValue - 1, dayValue, hoursValue, minutesValue, 0, 0);
  if (Number.isNaN(next.getTime())) {
    return null;
  }

  return formatLocalDateTime(next);
};

const ChevronDownIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const RemoveIcon = () => (
  <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="10" cy="10" r="7" />
    <path d="M7.3 7.3 12.7 12.7" strokeLinecap="round" />
    <path d="M12.7 7.3 7.3 12.7" strokeLinecap="round" />
  </svg>
);

const AddIcon = () => (
  <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6.5v7" strokeLinecap="round" />
    <path d="M6.5 10h7" strokeLinecap="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3.5" y="4.5" width="13" height="12" rx="2.5" />
    <path d="M6.5 2.8v3.4" strokeLinecap="round" />
    <path d="M13.5 2.8v3.4" strokeLinecap="round" />
    <path d="M3.5 8.5h13" strokeLinecap="round" />
  </svg>
);

const AcceptProposalIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m5.5 10.2 3 3 6-6.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DenyProposalIcon = () => (
  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m6.5 6.5 7 7M13.5 6.5l-7 7" strokeLinecap="round" />
  </svg>
);

type SelectFieldProps = {
  value: string;
  options: string[];
  onChange: (value: string) => void;
  getOptionLabel?: (option: string) => string;
  ariaLabel?: string;
  selectedLabel?: string;
  disabled?: boolean;
};

function SelectControl({ value, options, onChange, getOptionLabel, ariaLabel, selectedLabel, disabled }: SelectFieldProps) {
  return (
    <div className={`chain-select${selectedLabel ? ' chain-select--initials' : ''}`}>
      <select
        aria-label={ariaLabel}
        title={selectedLabel && getOptionLabel ? getOptionLabel(value) : undefined}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full appearance-none rounded-xl border border-[#f1ece4] bg-[#faf8f4] px-4 pr-10 text-base font-medium text-slate-700 outline-none transition focus:border-[#d1bf9b]"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {getOptionLabel ? getOptionLabel(option) : option}
          </option>
        ))}
      </select>
      {selectedLabel ? <span className="chain-select__initials" aria-hidden="true">{selectedLabel}</span> : null}
      <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-slate-400">
        <ChevronDownIcon />
      </span>
    </div>
  );
}

type ChainAppointmentCardProps = {
  appointment: ChainAppointment;
  proposedDate?: string;
  index: number;
  practitionerIds: string[];
  onPractitionerChange: (appointmentId: string, practitionerId: string) => void;
  onSelectAgendaSlot: (procedureId: string) => void;
  onTimeChange: (appointmentId: string, timeValue: string) => void;
  onDurationChange: (appointmentId: string, durationValue: string) => void;
  onDescriptionChange: (appointmentId: string, description: string) => void;
  onInfoChange: (appointmentId: string, key: 'min' | 'max', value: string) => void;
  onProcedureChange: (appointmentId: string, procedureId: string) => void;
  onRemove: (appointmentId: string) => void;
  onAcceptProposal: (appointmentId: string) => void;
  onDenyProposal: (appointmentId: string) => void;
  procedureOptions: Procedure[];
  getPractitionerLabel: (practitionerId: string) => string;
  getProcedureLabel: (procedureId: string, fallbackIndex: number) => string;
};

function ChainAppointmentCard({
  appointment,
  proposedDate,
  index,
  practitionerIds,
  onPractitionerChange,
  onSelectAgendaSlot,
  onTimeChange,
  onDurationChange,
  onDescriptionChange,
  onInfoChange,
  onProcedureChange,
  onRemove,
  onAcceptProposal,
  onDenyProposal,
  procedureOptions,
  getPractitionerLabel,
  getProcedureLabel,
}: ChainAppointmentCardProps) {
  const isFirstAppointment = index === 0;
  const displayedWhen = proposedDate || appointment.when;
  const displayDate = formatAppointmentDate(displayedWhen);
  const displayTime = formatAppointmentTime(displayedWhen);
  const displayDuration = appointment.duration > 0 ? String(appointment.duration) : '';
  const [timeDraft, setTimeDraft] = useState(displayTime);
  const [additionalCategory, setAdditionalCategory] = useState<string | null>(null);
  const selectedAdditionalProcedure = procedureOptions.find((procedure) => procedure.id === appointment.procedureIds[0]);
  const displayedAdditionalCategory = additionalCategory ?? selectedAdditionalProcedure?.category ?? '';
  const additionalProcedureOptions = displayedAdditionalCategory
    ? procedureOptions.filter((procedure) => procedure.category === displayedAdditionalCategory)
    : procedureOptions;


  useEffect(() => {
    setTimeDraft(displayTime);
  }, [displayTime]);

  const practitionerField = (
    <label className="chain-field chain-appointment__practitioner">
      <SelectControl
        ariaLabel={`Behandler für Termin ${index + 1}`}
        value={appointment.practitionerId}
        options={practitionerIds}
        onChange={(practitionerId) => onPractitionerChange(appointment.id, practitionerId)}
        getOptionLabel={getPractitionerLabel}
      />
    </label>
  );

  return (
    <section className={`chain-appointment${isFirstAppointment ? ' chain-appointment--first' : ''}${appointment.isManuallyAdded ? ' chain-appointment--additional' : ''}`} aria-label={`Termin ${index + 1}`}>
      <div className="chain-appointment__heading">
        <span className="chain-appointment__number">{index + 1}</span>
        <h2>{appointment.isManuallyAdded ? 'Zusatztermin' : getProcedureLabel(appointment.procedureIds[0] ?? '', index)}</h2>
        <input
          className="chain-appointment__description"
          aria-label={`Beschreibung für Termin ${index + 1}`}
          placeholder="Beschreibung eingeben"
          value={appointment.description}
          style={{ width: `${Math.max(9, (appointment.description || 'Beschreibung eingeben').length + 2)}ch` }}
          onChange={(event) => onDescriptionChange(appointment.id, event.target.value)}
        />
        <button type="button" className="chain-appointment__remove" onClick={() => onRemove(appointment.id)} aria-label={`Termin ${index + 1} entfernen`}>
          <RemoveIcon />
        </button>
      </div>

      <div className="chain-appointment__schedule">
        <div className="chain-field chain-appointment__date">
          <div className="chain-appointment__date-control">
            {proposedDate ? (
              <span className="chain-numeric">{displayDate}</span>
            ) : (
              <button type="button" className="chain-appointment__date-button" onClick={() => onSelectAgendaSlot(appointment.id)} aria-label={`Datum für Termin ${index + 1} wählen`}>
                {displayDate ? <span className="chain-numeric">{displayDate}</span> : 'Datum wählen'}
              </button>
            )}
            <span className="chain-appointment__separator" aria-hidden="true">·</span>
            <span className="chain-appointment__time">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                <circle cx="10" cy="10" r="7" /><path d="M10 6v4l3 2" strokeLinecap="round" />
              </svg>
              {proposedDate ? <span className="chain-numeric">{displayTime}</span> : (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{2}:[0-9]{2}"
                  aria-label={`Uhrzeit für Termin ${index + 1}`}
                  value={timeDraft}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setTimeDraft(nextValue);
                    if (/^\d{1,2}:\d{2}$/.test(nextValue)) onTimeChange(appointment.id, nextValue);
                  }}
                  onBlur={() => {
                    if (/^\d{1,2}:\d{2}$/.test(timeDraft)) onTimeChange(appointment.id, timeDraft);
                    else setTimeDraft(displayTime);
                  }}
                  placeholder="–:–"
                />
              )}
              <span>Uhr</span>
            </span>
            <span className="chain-appointment__separator" aria-hidden="true">·</span>
            <span className="chain-appointment__duration">
              <input
                type="number" min="0" step="5"
                aria-label={`Dauer für Termin ${index + 1} in Minuten`}
                value={displayDuration}
                onChange={(event) => onDurationChange(appointment.id, event.target.value)}
                placeholder="0"
              />
              <span>min</span>
            </span>
            {proposedDate ? (
              <div className="chain-appointment__proposal">
                <span>Vorschlag</span>
                <button type="button" className="chain-appointment__accept" onClick={() => onAcceptProposal(appointment.id)} aria-label={`Vorschlag für Termin ${index + 1} annehmen`}><AcceptProposalIcon /></button>
                <button type="button" className="chain-appointment__deny" onClick={() => onDenyProposal(appointment.id)} aria-label={`Vorschlag für Termin ${index + 1} ablehnen`}><DenyProposalIcon /></button>
              </div>
            ) : (
              <button type="button" className="chain-appointment__calendar" onClick={() => onSelectAgendaSlot(appointment.id)} aria-label={`Termin ${index + 1} in der Agenda planen`}><CalendarIcon /></button>
            )}
          </div>
        </div>
        {!appointment.isManuallyAdded ? practitionerField : null}
      </div>

      {appointment.isManuallyAdded ? (
        <div className="chain-appointment__additional-selectors">
          <label className="chain-field">
            <span>Kategorie</span>
            <SelectControl
              ariaLabel={`Kategorie für Termin ${index + 1}`}
              value={displayedAdditionalCategory}
              options={['', ...allCategoryItems]}
              getOptionLabel={(category) => category ? formatCategoryLabel(category) : 'Auswählen'}
              onChange={(category) => {
                setAdditionalCategory(category);
                if (category && selectedAdditionalProcedure && selectedAdditionalProcedure.category !== category) {
                  onProcedureChange(appointment.id, '');
                  // Changing the filter must not erase the configured day intervals.
                  for (const key of ['min', 'max'] as const) {
                    const value = appointment.appointmentInfoMap[key];
                    if (value !== undefined) onInfoChange(appointment.id, key, value);
                  }
                }
              }}
            />
          </label>
          <label className="chain-field">
            <span>Behandlung</span>
            <SelectControl
              ariaLabel={`Behandlung für Termin ${index + 1}`}
              value={appointment.procedureIds[0] ?? ''}
              options={['', ...additionalProcedureOptions.map((procedure) => procedure.id)]}
              disabled={Boolean(displayedAdditionalCategory) && additionalProcedureOptions.length === 0}
              onChange={(procedureId) => {
                onProcedureChange(appointment.id, procedureId);
                if (procedureId) setAdditionalCategory(null);
              }}
              getOptionLabel={(procedureId) => procedureId
                ? formatProcedureLabel(procedureOptions.find((procedure) => procedure.id === procedureId)?.name) || procedureId
                : displayedAdditionalCategory && additionalProcedureOptions.length === 0 ? 'Keine Behandlungen' : 'Auswählen'}
            />
          </label>
          {practitionerField}
        </div>
      ) : null}

      {!isFirstAppointment ? (
        <div className="chain-appointment__intervals">
          <label className="chain-field">
            <span>Min. Tage</span>
            <input className="chain-numeric" inputMode="numeric" value={appointment.appointmentInfoMap.min ?? ''} onChange={(event) => onInfoChange(appointment.id, 'min', event.target.value)} aria-label={`Min. Tage für Termin ${index + 1}`} />
          </label>
          <label className="chain-field">
            <span>Max. Tage</span>
            <input className="chain-numeric" inputMode="numeric" value={appointment.appointmentInfoMap.max ?? ''} onChange={(event) => onInfoChange(appointment.id, 'max', event.target.value)} aria-label={`Max. Tage für Termin ${index + 1}`} />
          </label>
        </div>
      ) : null}
    </section>
  );
}

export function AppointmentChainPage() {
  const navigate = useNavigate();
  const [restoredDraft] = useState(readAppointmentChainDraft);
  const restoredDraftRef = useRef<AppointmentChainDraft | null>(restoredDraft);
  const isRestoringDraftRef = useRef(Boolean(restoredDraftRef.current));
  const skipInitialPractitionerPropagationRef = useRef(Boolean(restoredDraftRef.current));
  const [selectedPatient, setSelectedPatient] = useState<FullUser | null>(restoredDraft?.selectedPatient ?? null);
  const [selectedCategory, setSelectedCategory] = useState(restoredDraft?.selectedCategory || categoryItems[0]);
  const [selectedMainProcedureId, setSelectedMainProcedureId] = useState(restoredDraft?.selectedMainProcedureId ?? '');
  const [selectedPractitionerId, setSelectedPractitionerId] = useState(
    restoredDraft?.selectedPractitionerId || readStoredSelectedPractitionerId() || practitionerIds[0]
  );
  const selectedPractitionerIdRef = useRef(selectedPractitionerId);
  selectedPractitionerIdRef.current = selectedPractitionerId;
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [resetRevision, setResetRevision] = useState(0);
  const [singleProcedures, setSingleProcedures] = useState<Procedure[]>([]);
  const [chainProcedures, setChainProcedures] = useState<Procedure[]>([]);
  const [chainAppointments, setChainAppointments] = useState<ChainAppointment[]>(() => restoreChainAppointments(restoredDraft));
  const [proposedDates, setProposedDates] = useState<string[]>(restoredDraft?.proposedDates ?? []);
  const [practitionersById, setPractitionersById] = useState<Record<string, FullPractitioner>>({});
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [treatmentsError, setTreatmentsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');

  useEffect(() => {
    if (restoredDraft?.selectedPatientId) {
      localStorage.setItem('selectedPatientId', restoredDraft.selectedPatientId);
    }
  }, [restoredDraft]);

  useEffect(() => {
    const resetAppointmentChain = () => {
      restoredDraftRef.current = null;
      isRestoringDraftRef.current = false;
      skipInitialPractitionerPropagationRef.current = false;
      setSelectedPatient(null);
      setResetRevision((currentRevision) => currentRevision + 1);
      setSelectedCategory(categoryItems[0]);
      setSelectedMainProcedureId('');
      setSelectedPractitionerId(readStoredSelectedPractitionerId() || practitionerIds[0]);
      setProcedures([]);
      setChainProcedures([]);
      setChainAppointments([]);
      setProposedDates([]);
      setTreatmentsError('');
      setError('');
      setSaveErrorMessage('');
    };

    window.addEventListener(appointmentChainResetEvent, resetAppointmentChain);
    return () => window.removeEventListener(appointmentChainResetEvent, resetAppointmentChain);
  }, []);

  const cancelAppointmentChain = () => {
    localStorage.removeItem('currentAppointmentChain');
    navigate('/agenda');
  };

  const storeAppointmentChain = async () => {
    console.log('storeAppointmentChain: ');
    setSaveErrorMessage('');

    const patientId = localStorage.getItem('selectedPatientId')?.trim() || '';
    const token = localStorage.getItem('token');

    // if (!patientId) {
    //   setError('Bitte Patient:in auswählen.');
    //   return;
    // }

    const appointmentsToStore = chainAppointments
      .map((appointment) => {
        const when = appointment.when?.trim() || '';
        const practitionerId = appointment.practitionerId?.trim() || '';
        const procedureId = appointment.procedureIds[0]?.trim() || '';
        const duration = Number(appointment.duration);
        const description = appointment.description.trim();

        if (!when || !practitionerId || !procedureId || !Number.isFinite(duration) || duration <= 0) {
          return null;
        }

        return {
          when,
          duration,
          practitionerId,
          patientId,
          procedureId,
          appointmentInfoMap: appointment.appointmentInfoMap,
          ...(description ? { description } : {}),
          mode: "ergodent:demo",
        };
      });

    if (appointmentsToStore.some((appointment) => appointment === null)) {
      setError('Bitte alle Termine vollständig ausfüllen.');
      return;
    }

    if (appointmentsToStore.length === 0) {
      setError('Keine Termine zum Speichern vorhanden.');
      return;
    }

    console.log("Store initiated: " + JSON.stringify(appointmentsToStore));

    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/appointments/save-chain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ appointments: appointmentsToStore }),
      });

      const responseBody = await readAppointmentResponseBody(response);
      const saveSuccessful = isSuccessfulAppointmentResponse(response, responseBody);

      if (!saveSuccessful) {
        throw formatAppointmentResponseBody(responseBody);
      }

      localStorage.removeItem('currentAppointmentChain');
      navigate('/agenda');
    } catch (saveError) {
      console.error('Failed to save appointment chain', saveError);
      if (typeof saveError === 'string') {
        setSaveErrorMessage(`Error: ${saveError}`);
      } else {
        setError('Terminserie konnte nicht gespeichert werden.');
      }
    }

    /* the old code using /appointments/save-full:
    try {
      await Promise.all(
        appointmentsToStore.map(async (appointment) => {
          const response = await fetch(`${API_BASE_URL}/appointments/save-full`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(appointment),
          });

          const responseBody = await readAppointmentResponseBody(response);
          const saveSuccessful = isSuccessfulAppointmentResponse(response, responseBody);

          if (!saveSuccessful) {
            throw formatAppointmentResponseBody(responseBody);
          }
        })
      );

      navigate('/agenda');
    } catch (saveError) {
      console.error('Failed to save appointment chain', saveError);
      if (typeof saveError === 'string') {
        setSaveErrorMessage(`Error: ${saveError}`);
      } else {
        setError('Terminserie konnte nicht gespeichert werden.');
      }
    }
    */

  };

  const hasSelectedPatientId = Boolean(localStorage.getItem('selectedPatientId')?.trim());

  const handlePractitionerChange = (appointmentId: string, practitionerId: string) => {
    setChainAppointments((currentAppointments) => {
      const nextAppointments = currentAppointments.map((appointment) =>
        appointment.id === appointmentId
          ? {
            ...appointment,
            practitionerId,
          }
          : appointment
      );

      console.log("chainAppointments(next)=" + JSON.stringify(nextAppointments));
      return nextAppointments;
    });
  };

  const handleRemoveAppointment = (appointmentId: string) => {
    setChainAppointments((currentAppointments) =>
      currentAppointments.filter((appointment) => appointment.id !== appointmentId)
    );
  };

  const handleAddAppointment = (precedingAppointmentId: string) => {
    setChainAppointments((currentAppointments) => {
      const precedingIndex = currentAppointments.findIndex((appointment) => appointment.id === precedingAppointmentId);
      if (precedingIndex < 0) {
        return currentAppointments;
      }

      const newAppointment: ChainAppointment = {
        id: `new-appointment-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        procedureIds: [],
        duration: 0,
        description: '',
        appointmentInfoMap: { chainTitle: 'addon' },
        practitionerId: selectedPractitionerId,
        isManuallyAdded: true,
      };
      const nextAppointments = [...currentAppointments];
      nextAppointments.splice(precedingIndex + 1, 0, newAppointment);
      return nextAppointments;
    });
  };

  const handleAddAppointmentToHead = () => {
    setChainAppointments((currentAppointments) => {
      const newAppointment: ChainAppointment = {
        id: `new-appointment-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        procedureIds: [],
        duration: 0,
        description: '',
        appointmentInfoMap: { chainTitle: 'addon' },
        practitionerId: selectedPractitionerId,
        isManuallyAdded: true,
      };

      const appointmentsWithUpdatedHead = currentAppointments.map((appointment, index) =>
        index === 0
          ? {
            ...appointment,
            appointmentInfoMap: {
              ...appointment.appointmentInfoMap,
              min: '5',
              max: '10',
            },
          }
          : appointment
      );

      return [newAppointment, ...appointmentsWithUpdatedHead];
    });
  };

  const handleAppointmentProcedureChange = (appointmentId: string, procedureId: string) => {
    const selectedProcedure = singleProcedures.find((procedure) => procedure.id === procedureId);

    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) =>
        appointment.id === appointmentId
          ? {
            ...appointment,
            procedureIds: procedureId ? [procedureId] : [],
            duration: selectedProcedure ? Number(selectedProcedure.duration) || 0 : 0,
            appointmentInfoMap: {
              ...(selectedProcedure?.ruleMap ?? {}),
              ...(appointment.appointmentInfoMap.chainTitle
                ? { chainTitle: appointment.appointmentInfoMap.chainTitle }
                : {}),
            },
          }
          : appointment
      )
    );
  };

  const handleSelectAgendaSlot = (procedureId: string) => {
    const editingIndex = chainAppointments.findIndex((candidate) => candidate.id === procedureId);
    if (editingIndex < 0) {
      return;
    }

    const draftChainAppointments = addProposedDatesToDraftAppointments(chainAppointments, proposedDates);
    const appointment = draftChainAppointments[editingIndex];
    const selectedPatientId = localStorage.getItem('selectedPatientId')?.trim() || restoredDraftRef.current?.selectedPatientId || '';
    const draft: AppointmentChainDraft = {
      version: 1,
      updatedAt: new Date().toISOString(),
      ...getDraftDateRange(draftChainAppointments, editingIndex),
      selectedCategory,
      selectedMainProcedureId,
      selectedPractitionerId,
      selectedPatient: selectedPatient
        ? {
          id: selectedPatient.id,
          title: selectedPatient.title,
          firstName: selectedPatient.firstName,
          familyName: selectedPatient.familyName,
          dateOfBirth: selectedPatient.dateOfBirth,
        }
        : null,
      selectedPatientId,
      chainAppointments: draftChainAppointments,
      proposedDates,
      editingProcedureId: procedureId,
      editingIndex,
      manualPlacement: true,
      editingProcedureName: formatProcedureLabel([...chainProcedures, ...singleProcedures].find((item) => item.id === appointment.procedureIds[0])?.name)
        || getChainProcedureLabel(appointment.procedureIds[0] ?? '', editingIndex),
    };

    //console.log(draft);

    localStorage.setItem('currentAppointmentChain', JSON.stringify(draft));
    // Open near the permitted window even after the rejected proposal was cleared.
    const preceding = editingIndex > 0 ? draftChainAppointments[editingIndex - 1]?.when : undefined;
    const initialDate = new Date(appointment?.when || preceding || draft.startDate);
    if (!appointment?.when && preceding) {
      const minDays = Number(appointment.appointmentInfoMap.min);
      if (Number.isFinite(minDays) && minDays >= 0) initialDate.setDate(initialDate.getDate() + minDays);
    }
    if (!Number.isNaN(initialDate.getTime())) localStorage.setItem('agendaDate', formatLocalDateTime(initialDate));
    navigate('/agenda-weekly');
  };

  const handleAppointmentTimeChange = (appointmentId: string, timeValue: string) => {
    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) => {
        if (appointment.id !== appointmentId || !timeValue) {
          return appointment;
        }

        const fallbackDate = appointment.when ? formatDateInputValue(appointment.when) : formatCurrentDateInputValue();
        const nextWhen = buildAppointmentDateTime(fallbackDate, timeValue);

        return {
          ...appointment,
          when: nextWhen ?? appointment.when,
        };
      })
    );
  };

  const handleAcceptProposal = (appointmentId: string) => {
    const appointmentIndex = chainAppointments.findIndex((appointment) => appointment.id === appointmentId);
    const proposedDate = proposedDates[appointmentIndex];
    if (!proposedDate) {
      return;
    }

    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) =>
        appointment.id === appointmentId ? { ...appointment, when: proposedDate } : appointment
      )
    );
    setProposedDates((currentDates) =>
      currentDates.map((date, index) => index === appointmentIndex ? '' : date)
    );
  };

  const handleDenyProposal = (appointmentId: string) => {
    const appointmentIndex = chainAppointments.findIndex((appointment) => appointment.id === appointmentId);
    if (appointmentIndex < 0) {
      return;
    }

    setProposedDates((currentDates) =>
      currentDates.map((date, index) => index === appointmentIndex ? '' : date)
    );
  };

  const handleAppointmentDurationChange = (appointmentId: string, durationValue: string) => {
    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) => {
        if (appointment.id !== appointmentId) {
          return appointment;
        }

        const parsedDuration = Number(durationValue);
        return {
          ...appointment,
          duration: Number.isFinite(parsedDuration) && parsedDuration >= 0 ? parsedDuration : 0,
        };
      })
    );
  };

  const handleAppointmentDescriptionChange = (appointmentId: string, description: string) => {
    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) =>
        appointment.id === appointmentId
          ? {
            ...appointment,
            description,
          }
          : appointment
      )
    );
  };

  const handleAppointmentInfoChange = (appointmentId: string, key: 'min' | 'max', value: string) => {
    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) =>
        appointment.id === appointmentId
          ? {
            ...appointment,
            appointmentInfoMap: {
              ...appointment.appointmentInfoMap,
              [key]: value,
            },
          }
          : appointment
      )
    );
  };

  const getPractitionerOptionLabel = (practitionerId: string) =>
    getPractitionerInitials(practitionersById[practitionerId] ?? practitionerCache.get(practitionerId));

  const getPractitionerFullNameLabel = (practitionerId: string) =>
    getPractitionerFullName(practitionersById[practitionerId] ?? practitionerCache.get(practitionerId)) || practitionerId;

  const getChainProcedureLabel = (procedureId: string, fallbackIndex: number) => {
    const procedure = chainProcedures.find((chainProcedure) => chainProcedure.id === procedureId);
    return formatProcedureLabel(procedure?.name) || `Termin ${fallbackIndex + 1}`;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchSingleProcedures = async () => {
      const token = localStorage.getItem('token');

      try {
        const proceduresByCategory = await Promise.all(
          allCategoryItems.map(async (category) => {
            const response = await fetch(`${API_BASE_URL}/procedures/search`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({ category, type: 'single' }),
            });

            if (!response.ok) {
              throw new Error(`Bad procedure search response for ${category}: ${response.status}`);
            }

            const resolvedProcedures = (await response.json()) as Procedure[];
            return Array.isArray(resolvedProcedures) ? resolvedProcedures : [];
          })
        );

        if (!isMounted) {
          return;
        }

        setSingleProcedures(
          Array.from(
            new Map(proceduresByCategory.flat().map((procedure) => [procedure.id, procedure])).values()
          )
        );
      } catch (loadError) {
        console.error('Failed to load procedures for additional appointments', loadError);
      }
    };

    void fetchSingleProcedures();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchProceduresByCategory = async () => {
      if (!selectedCategory) {
        setProcedures([]);
        setSelectedMainProcedureId('');
        setTreatmentsLoading(false);
        setTreatmentsError('');
        return;
      }

      setTreatmentsLoading(true);
      setTreatmentsError('');
      setProcedures([]);

      const token = localStorage.getItem('token');

      try {
        const response = await fetch(`${API_BASE_URL}/procedures/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ category: selectedCategory, type: 'series' }),
        });

        if (!response.ok) {
          throw new Error(`Bad procedure search response: ${response.status}`);
        }

        const resolvedProcedures = (await response.json()) as Procedure[];

        console.log("resolvedProcedures(A)=" + JSON.stringify(resolvedProcedures));

        if (!isMounted) {
          return;
        }

        const nextProcedures = (Array.isArray(resolvedProcedures) ? resolvedProcedures : []).filter(isMainProcedure);
        setProcedures(nextProcedures);
        setSelectedMainProcedureId((currentProcedureId) =>
          nextProcedures.some((procedure) => procedure.id === currentProcedureId)
            ? currentProcedureId
            : nextProcedures[0]?.id ?? ''
        );
      } catch (loadError) {
        console.error('Failed to load series procedures', loadError);

        if (!isMounted) {
          return;
        }

        setTreatmentsError('Behandlungen konnten nicht geladen werden.');
      } finally {
        if (isMounted) {
          setTreatmentsLoading(false);
        }
      }
    };

    fetchProceduresByCategory();

    console.log("procedures=" + JSON.stringify(procedures));

    return () => {
      isMounted = false;
    };
  }, [selectedCategory, resetRevision]);

  useEffect(() => {
    let isMounted = true;

    const fetchProcedures = async () => {
      if (!selectedCategory || !selectedMainProcedureId) {
        setChainProcedures([]);
        setChainAppointments([]);
        setLoading(false);
        setError('');
        return;
      }

      const token = localStorage.getItem('token');
      const storedSelectedDateTime = localStorage.getItem('selectedDateTime') ?? undefined;
      const shouldPreserveRestoredChain = Boolean(
        isRestoringDraftRef.current &&
        restoredDraftRef.current?.selectedCategory === selectedCategory &&
        restoredDraftRef.current?.selectedMainProcedureId === selectedMainProcedureId
      );
      setChainProcedures([]);
      if (!shouldPreserveRestoredChain) {
        setChainAppointments([]);
      }
      setLoading(true);
      setError('');

      try {
        const mainProcedureResponse = await fetch(`${API_BASE_URL}/procedures/${selectedMainProcedureId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!mainProcedureResponse.ok) {
          throw new Error(`Bad procedure response for ${selectedMainProcedureId}: ${mainProcedureResponse.status}`);
        }

        const mainProcedure = (await mainProcedureResponse.json()) as Procedure;
        const linkedProcedureIds = getLinkedProcedureIds(mainProcedure);
        const linkedProcedures = await Promise.all(
          linkedProcedureIds.map(async (linkedProcedureId) => {
            const response = await fetch(`${API_BASE_URL}/procedures/${linkedProcedureId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
              throw new Error(`Bad procedure response for ${linkedProcedureId}: ${response.status}`);
            }

            return (await response.json()) as Procedure;
          })
        );
        const resolvedProcedures = [mainProcedure, ...linkedProcedures];

        if (!isMounted) {
          return;
        }

        setChainProcedures(resolvedProcedures);

        //console.log("resolvedProcedures(B)=" + JSON.stringify(resolvedProcedures));

        if (!shouldPreserveRestoredChain) {
          setChainAppointments(
            resolvedProcedures.map((procedure, index) => ({
              id: procedure.id || `appointment-${index + 1}`,
              procedureIds: [procedure.id || ''],
              when: index === 0 ? storedSelectedDateTime : undefined,
              duration: Number(procedure.duration) || 0,
              description: '',
              appointmentInfoMap: {
                ...procedure.ruleMap,
                ...(procedure.ruleMap.linkedIds !== undefined
                  ? { linkedIds: normalizeLinkedProcedureIds(procedure.ruleMap.linkedIds) }
                  : {}),
              },
              practitionerId: selectedPractitionerIdRef.current,
            }))
          );
        }
        isRestoringDraftRef.current = false;
        setError('');

        //console.log("chainProcedures=" + JSON.stringify(chainProcedures));

      } catch (loadError) {
        console.error('Failed to load appointment chain procedures', loadError);

        if (!isMounted) {
          return;
        }

        setChainProcedures([]);
        if (!shouldPreserveRestoredChain) {
          setChainAppointments(
            [selectedMainProcedureId].map((procedureId, index) => ({
              id: procedureId,
              procedureIds: [procedureId],
              when: index === 0 ? storedSelectedDateTime : undefined,
              duration: 0,
              description: '',
              appointmentInfoMap: {
                min: '',
                max: '',
              },
              practitionerId: selectedPractitionerIdRef.current,
            }))
          );
        }
        isRestoringDraftRef.current = false;
        setError('Behandlungen konnten nicht geladen werden.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProcedures();

    return () => {
      isMounted = false;
    };
  }, [selectedCategory, selectedMainProcedureId]);

  useEffect(() => {
    console.log("chainProcedures(updated)=" + JSON.stringify(chainProcedures));
  }, [chainProcedures]);

  useEffect(() => {
    console.log("chainAppointments(updated)=" + JSON.stringify(chainAppointments));
  }, [chainAppointments]);

  useEffect(() => {
    const selectedPatientId = localStorage.getItem('selectedPatientId')?.trim() || '';
    if (!selectedPatient || !selectedPatientId || !selectedPractitionerId) {
      setProposedDates([]);
      return;
    }

    const hasIncompleteManuallyAddedAppointment = chainAppointments.some((appointment) =>
      appointment.isManuallyAdded &&
      (!appointment.appointmentInfoMap.min?.trim() || !appointment.appointmentInfoMap.max?.trim())
    );
    if (hasIncompleteManuallyAddedAppointment) {
      return;
    }

    const abortController = new AbortController();
    const token = localStorage.getItem('token');

    const proposeChainDates = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/appointments/propose-chain-dates`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            patientId: selectedPatientId,
            chainAppointments,
          }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Bad proposed chain dates response: ${response.status}`);
        }

        const proposedDatesResponse = (await response.json()) as ProposedChainDatesResult;
        console.log(proposedDatesResponse.message);
        console.log(proposedDatesResponse.dateChoices[0]);

        const firstDateChoice = Array.isArray(proposedDatesResponse.dateChoices?.[0])
          ? proposedDatesResponse.dateChoices[0]
          : [];
        setProposedDates(
          chainAppointments.map((appointment, index) => appointment.when ? '' : firstDateChoice[index] || '')
        );
      } catch (proposeError) {
        if (!abortController.signal.aborted) {
          console.error('Failed to propose appointment chain dates', proposeError);
          setProposedDates([]);
        }
      }
    };

    proposeChainDates();

    return () => abortController.abort();
  }, [selectedPatient, selectedPractitionerId, chainAppointments]);

  useEffect(() => {
    let isMounted = true;

    const fetchPractitioners = async () => {
      const token = localStorage.getItem('token');
      const cachedPractitioners = practitionerIds.reduce<Record<string, FullPractitioner>>((accumulator, practitionerId) => {
        const cachedPractitioner = practitionerCache.get(practitionerId);

        if (cachedPractitioner) {
          accumulator[practitionerId] = cachedPractitioner;
        }

        return accumulator;
      }, {});

      if (Object.keys(cachedPractitioners).length === practitionerIds.length) {
        setPractitionersById(cachedPractitioners);
        return;
      }

      setPractitionersById(cachedPractitioners);

      try {
        const missingPractitionerIds = practitionerIds.filter((practitionerId) => !practitionerCache.has(practitionerId));
        const fetchedPractitioners = await Promise.all(
          missingPractitionerIds.map(async (practitionerId) => {
            const response = await fetch(`${API_BASE_URL}/practitioners/full/${practitionerId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });

            if (!response.ok) {
              throw new Error(`Bad practitioner response for ${practitionerId}: ${response.status}`);
            }

            const fullPractitioner = (await response.json()) as FullPractitioner;
            practitionerCache.set(practitionerId, fullPractitioner);

            console.log("fullPractitioner=" + JSON.stringify(fullPractitioner));

            return [practitionerId, fullPractitioner] as const;
          })
        );

        if (!isMounted) {
          return;
        }

        setPractitionersById({
          ...cachedPractitioners,
          ...Object.fromEntries(fetchedPractitioners),
        });
      } catch (loadError) {
        console.error('Failed to load practitioners for appointment chain', loadError);
      }
    };

    fetchPractitioners();

    //console.log("chainProcedures=" + JSON.stringify(chainProcedures));

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedPractitionerId', selectedPractitionerId);
    if (skipInitialPractitionerPropagationRef.current) {
      skipInitialPractitionerPropagationRef.current = false;
      return;
    }

    setChainAppointments((currentAppointments) =>
      currentAppointments.map((appointment) => ({
        ...appointment,
        practitionerId: selectedPractitionerId,
      }))
    );
  }, [selectedPractitionerId]);

  useEffect(() => {
    const selectedPatientId = localStorage.getItem('selectedPatientId')?.trim() || restoredDraftRef.current?.selectedPatientId || '';
    const storedPatient = selectedPatient
      ? {
        id: selectedPatient.id,
        title: selectedPatient.title,
        firstName: selectedPatient.firstName,
        familyName: selectedPatient.familyName,
        dateOfBirth: selectedPatient.dateOfBirth,
      }
      : null;
    const draft: AppointmentChainDraft = {
      version: 1,
      updatedAt: new Date().toISOString(),
      ...getDraftDateRange(),
      selectedCategory,
      selectedMainProcedureId,
      selectedPractitionerId,
      selectedPatient: storedPatient,
      selectedPatientId,
      chainAppointments: addProposedDatesToDraftAppointments(chainAppointments, proposedDates),
      proposedDates,
    };

    localStorage.setItem('currentAppointmentChain', JSON.stringify(draft));
  }, [selectedCategory, selectedMainProcedureId, selectedPractitionerId, selectedPatient, chainAppointments, proposedDates]);

  return (
    <div className="portal-page-surface appointment-chain-page text-slate-800">
      <AppointmentResponseErrorDialog message={saveErrorMessage} onClose={() => setSaveErrorMessage('')} />
      <div className="appointment-chain__card">
        <h1>Terminserie erstellen</h1>

        <section className="appointment-chain__patient">
          <div className="chain-field">
            <label htmlFor="appointment-chain-patient"><span>Patient</span></label>
            <AdminPatientSearch
              key={resetRevision}
              id="appointment-chain-patient"
              selectedPatient={selectedPatient}
              onSelect={setSelectedPatient}
              onReset={() => { restoredDraftRef.current = null; setSelectedPatient(null); }}
            />
          </div>
          <div className="appointment-chain__patient-shortcuts">
            <button type="button" disabled title="Noch nicht verfügbar">Letzte(r)</button>
            <button type="button" disabled title="Noch nicht verfügbar">Neueste(r)</button>
          </div>
        </section>

        <section className="appointment-chain__selectors">
          <label className="chain-field">
            <span>Kategorie</span>
            <SelectControl
              ariaLabel="Kategorie"
              value={selectedCategory}
              options={categoryItems}
              onChange={(category) => { isRestoringDraftRef.current = false; setSelectedCategory(category); }}
              getOptionLabel={formatCategoryLabel}
            />
          </label>
          <label className="chain-field">
            <span>Behandlung</span>
            <SelectControl
              ariaLabel="Behandlung"
              value={selectedMainProcedureId}
              options={procedures.length ? procedures.map((procedure) => procedure.id) : ['']}
              disabled={treatmentsLoading || !procedures.length}
              onChange={(procedureId) => { isRestoringDraftRef.current = false; setSelectedMainProcedureId(procedureId); }}
              getOptionLabel={(procedureId) => getProcedureChainTitle(procedures.find((procedure) => procedure.id === procedureId)) || formatProcedureLabel(procedures.find((procedure) => procedure.id === procedureId)?.name) || procedureId || 'Auswählen'}
            />
          </label>
          <label className="chain-field">
            <span>Behandler</span>
            <SelectControl
              ariaLabel="Behandler"
              value={selectedPractitionerId}
              options={practitionerIds}
              onChange={setSelectedPractitionerId}
              getOptionLabel={getPractitionerFullNameLabel}
              selectedLabel={getPractitionerOptionLabel(selectedPractitionerId)}
            />
          </label>
        </section>

        <div className="appointment-chain__appointments">
          {treatmentsLoading ? <p className="chain-message">Behandlungen werden geladen...</p> : null}
          {!treatmentsLoading && treatmentsError ? <p role="alert" className="text-sm text-red-600">{treatmentsError}</p> : null}
          {!treatmentsLoading && !treatmentsError && selectedCategory && procedures.length === 0 ? <p className="chain-message">Keine Terminserien gefunden.</p> : null}
          {loading ? <p className="chain-message">Behandlungen werden geladen...</p> : null}
          {!loading && error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
          {chainAppointments.length > 0 ? (
            <div className="appointment-chain__insert"><button type="button" onClick={handleAddAppointmentToHead} aria-label="Termin vor Termin 1 hinzufügen"><AddIcon /></button></div>
          ) : null}
          {chainAppointments.map((appointment, index) => (
            <Fragment key={appointment.id}>
              <ChainAppointmentCard
                appointment={appointment}
                proposedDate={proposedDates[index]}
                index={index}
                practitionerIds={practitionerIds}
                onPractitionerChange={handlePractitionerChange}
                onSelectAgendaSlot={handleSelectAgendaSlot}
                onTimeChange={handleAppointmentTimeChange}
                onDurationChange={handleAppointmentDurationChange}
                onDescriptionChange={handleAppointmentDescriptionChange}
                onInfoChange={handleAppointmentInfoChange}
                onProcedureChange={handleAppointmentProcedureChange}
                onRemove={handleRemoveAppointment}
                onAcceptProposal={handleAcceptProposal}
                onDenyProposal={handleDenyProposal}
                procedureOptions={singleProcedures}
                getPractitionerLabel={getPractitionerOptionLabel}
                getProcedureLabel={getChainProcedureLabel}
              />
              <div className="appointment-chain__insert"><button type="button" onClick={() => handleAddAppointment(appointment.id)} aria-label={`Termin nach Termin ${index + 1} hinzufügen`}><AddIcon /></button></div>
            </Fragment>
          ))}
        </div>

        <section className="appointment-chain__utilities">
          <div>
            <p>Terminkärtchen</p>
            <div className="appointment-chain__utility-buttons">
              <button type="button" disabled title="Noch nicht verfügbar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 9V4h10v5" /><rect x="6" y="14" width="12" height="6" rx="1.5" /><path d="M6 17H4.5A1.5 1.5 0 0 1 3 15.5v-4A1.5 1.5 0 0 1 4.5 10h15a1.5 1.5 0 0 1 1.5 1.5v4A1.5 1.5 0 0 1 19.5 17H18" /></svg>Druck</button>
              <button type="button" disabled title="Noch nicht verfügbar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="13" rx="2" /><path d="m5.5 8 6.5 5 6.5-5" strokeLinecap="round" strokeLinejoin="round" /></svg>Email</button>
              <button type="button" disabled title="Noch nicht verfügbar"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 6.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z" /></svg>SMS</button>
            </div>
          </div>
        </section>
        <div className="appointment-chain__actions">
          <button type="button" hidden onClick={cancelAppointmentChain}>Abbrechen</button>
          <button type="button" className="appointment-chain__save" onClick={storeAppointmentChain} disabled={!hasSelectedPatientId}>Termin speichern</button>
        </div>
      </div>
    </div>
  );
}
